import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { TrackingApi } from 'src/api';
import { Icon, Tooltip } from 'src/components';
import { useUserContext } from 'src/context/user';
import { SLOW_VIDEO_SPEED_LABEL } from 'src/utils/videoSpeed';
import theme from 'src/utils/theme';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import * as S from './styles';

const msgs = defineMessages({
  play: {
    id: 'learning_session_video_speed_play',
    defaultMessage: 'Play video at this speed',
  },
});

interface VideoSpeedControlProps {
  className?: string;
  onReplay: () => void;
  isPlayingSlowly?: boolean;
  languagePairId?: number;
  /** Hide the "0.50x" label beside the tortoise button. */
  hideLabel?: boolean;
}

export const VideoSpeedControl: FC<VideoSpeedControlProps> = ({
  className,
  onReplay,
  isPlayingSlowly = false,
  languagePairId,
  hideLabel = false,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const playSlowlyLabel = intl.formatMessage(msgs.play);

  const handleClick = () => {
    const wordlistId = getActiveWordlistId();
    const isProUser = !!user?.isPro;

    void TrackingApi.trackUserAction(
      'lwl_speed_button_clicked',
      'learning_session',
      languagePairId ?? null,
      isProUser,
      {
        is_pro_user: String(isProUser),
        ...(wordlistId && { wordlist_id: wordlistId }),
      },
    );

    onReplay();
  };

  return (
    <S.Container className={className} data-testid="videoSpeedControl">
      <Tooltip placement="bottom" tooltip={playSlowlyLabel} disabled={isMobile}>
        <S.SpeedButton
          type="button"
          data-testid="videoSpeedButton"
          aria-label={playSlowlyLabel}
          aria-pressed={isPlayingSlowly}
          $isPlayingSlowly={isPlayingSlowly}
          onClick={handleClick}
        >
          <Icon name="turtle" />
        </S.SpeedButton>
      </Tooltip>
      {!hideLabel && (
        <S.SpeedValueLabel data-testid="videoSpeedValueLabel">
          {SLOW_VIDEO_SPEED_LABEL}
        </S.SpeedValueLabel>
      )}
    </S.Container>
  );
};

export default VideoSpeedControl;
